import { cellText, headerIndex, normalizeHeader } from './cells';

const MAX_HEADER_ROWS = 4;

/**
 * Join stacked AS/400 header rows in one column (`PRODUCT` + `NO.` →
 * `PRODUCT NO.`). Duplicate fragments are dropped.
 */
export function mergeHeaderCells(rows: unknown[][]): string[] {
  const width = Math.max(0, ...rows.map((row) => row.length));
  const merged: string[] = [];
  for (let column = 0; column < width; column += 1) {
    const parts: string[] = [];
    for (const row of rows) {
      const text = normalizeHeader(row[column]);
      if (text !== '' && !parts.includes(text)) parts.push(text);
    }
    merged.push(parts.join(' '));
  }
  return merged;
}

/**
 * Like {@link headerIndex}, but also matches a joined stacked name that
 * starts or ends with `name` (`NUMBER` → `PRODUCT NUMBER`).
 */
export function headerIndexFlexible(
  headerRow: unknown[],
  name: string,
): number {
  const exact = headerIndex(headerRow, name);
  if (exact !== -1) return exact;
  const wanted = normalizeHeader(name);
  if (wanted === '') return -1;
  return headerRow.findIndex((cell) => {
    const current = normalizeHeader(cell);
    return current.endsWith(` ${wanted}`) || current.startsWith(`${wanted} `);
  });
}

export function stackedCell(
  row: unknown[],
  headerRow: unknown[],
): (header: string) => string {
  return function cell(header: string): string {
    const index = headerIndexFlexible(headerRow, header);
    if (index === -1) return '';
    return cellText(row[index]);
  };
}

function headerRowHasRequired(
  headerRow: unknown[],
  required: readonly string[],
): boolean {
  return required.every(
    (header) => headerIndexFlexible(headerRow, header) !== -1,
  );
}

/**
 * First 1–4 row window whose merged cells contain every required header.
 * `headerAt` is the last row of that window (data starts on the next row).
 */
export function findMergedHeader(
  rows: unknown[][],
  required: readonly string[],
): { headerAt: number; headerRow: unknown[] } | null {
  const maxEnd = Math.min(MAX_HEADER_ROWS, rows.length) - 1;
  for (let end = 0; end <= maxEnd; end += 1) {
    for (let start = 0; start <= end; start += 1) {
      const headerRow = mergeHeaderCells(rows.slice(start, end + 1));
      if (headerRowHasRequired(headerRow, required)) {
        return { headerAt: end, headerRow };
      }
    }
  }
  return null;
}
